import React from 'react';
import { PartyTag } from '../lower-order/PartyTag';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ColumnLabel } from '../../app/feathers/candidate-lists';

interface PartyOption {
  key: string;
  name: string;
}

interface CellRendererProps {
  columnLabel?: ColumnLabel;
  value: any;
  validationError?: string | null;
  partyOptions?: PartyOption[];
  onPartyRemedy?: (party: PartyOption) => void;
  style?: React.CSSProperties;
}

const CellRenderer: React.FC<CellRendererProps> = ({ 
  columnLabel, 
  value,
  validationError,
  partyOptions = [],
  onPartyRemedy,
  style = {} 
}) => {
  const [partyMenuAnchorEl, setPartyMenuAnchorEl] = React.useState<HTMLElement | null>(null);

  switch (columnLabel) {
    case 'party':
    case 'raceParty':
      if (validationError) {
        const displayValue = value === null || value === undefined ? '' : String(value);
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: '#d32f2f',
            ...style
          }}>
            <WarningIcon style={{ fontSize: '16px', color: '#f57c00', flexShrink: 0 }} />
            <Typography
              variant="body2"
              style={{
                fontSize: '12px',
                color: 'inherit'
              }}
            >
              {displayValue}
            </Typography>
            {onPartyRemedy && partyOptions.length > 0 && (
              <>
                <IconButton
                  size="small"
                  onClick={(event) => setPartyMenuAnchorEl(event.currentTarget)}
                  style={{ padding: 0 }}
                >
                  <ArrowDropDownIcon style={{ fontSize: '18px' }} />
                </IconButton>
                <Menu
                  anchorEl={partyMenuAnchorEl}
                  open={Boolean(partyMenuAnchorEl)}
                  onClose={() => setPartyMenuAnchorEl(null)}
                >
                  {partyOptions.map((party) => (
                    <MenuItem
                      key={party.key}
                      onClick={() => {
                        onPartyRemedy(party);
                        setPartyMenuAnchorEl(null);
                      }}
                    >
                      {party.name}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </div>
        );
      }

      return (
        <PartyTag 
          party={value} 
          preloadedParties={partyOptions}
          style={{ 
            fontSize: '10px',
            padding: '2px 4px'
          }}
        />
      );
    
    case 'incumbent':
    case 'withdrawn':
    case 'writeIn':
      return (
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: value ? '#4caf50' : '#f44336',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography 
              variant="caption" 
              style={{ 
                fontSize: '10px',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              {value ? '✓' : '✗'}
            </Typography>
        </div>
      );
    
    case 'website':
      if(!value) {
        return null;
      }
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px',
          backgroundColor: '#f5f5f5',
          borderRadius: '6px',
          cursor: 'pointer',
        }}
        onClick={() => {
          if (value && typeof value === 'string') {
            window.open(value, '_blank', 'noopener,noreferrer');
          }
        }}
        >
          <span style={{ fontSize: '12px' }}>🔗</span>
          <Typography 
            variant="body2" 
            style={{ 
              fontSize: '12px',
              color: '#333',
            }}
          >
            {value}
          </Typography>
        </div>
      );
    
    case 'email':
      if(!value) {
        return null;
      }
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px',
          backgroundColor: '#f5f5f5',
          borderRadius: '6px',
          cursor: 'pointer'
        }}
        onClick={() => {
          if (value && typeof value === 'string') {
            window.open(`mailto:${value}`, '_blank');
          }
        }}
        >
          <span style={{ fontSize: '12px' }}>✉️</span>
          <Typography 
            variant="body2" 
            style={{ 
              fontSize: '12px',
              color: '#333',
            }}
          >
            {value}
          </Typography>
        </div>
      );
    default: 
      const displayValue = value === null ? '' : 
                          value === true ? 'true' : 
                          value === false ? 'false' : 
                          value === undefined ? '' : 
                          String(value);
      return (
        <Typography 
          variant="body2" 
          style={{ 
            fontSize: '12px',
            color: 'inherit',
            ...style 
          }}
        >
          {displayValue}
        </Typography>
      );

  }
};

export default CellRenderer; 